const { h } = window.preact;
const { useState, useRef, useEffect } = window.preactHooks;
const html = window.htm.bind(h);

import { useRollenfinder } from "./rollenfinder-context.js";
import { Spinner } from "./Spinner.js";
import { getTranslation } from "./translations.js";

async function getModels(manufacturers) {
  const response = await fetch(
    `/apps/rollenfinder/fitting-printers?manufacturers=${manufacturers.join(",")}`,
  );
  const data = await response.json();
  return data.printers || [];
}

export function RollenfinderStep2() {
  const { state, updateState } = useRollenfinder();
  const [searchQuery, setSearchQuery] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        setIsLoading(true);
        const selectedManufacturerNames = state.selectedManufacturers.map(
          (m) => m.name,
        );
        const models = await getModels(selectedManufacturerNames);
        setAvailableModels(models);
        setFilteredModels(models);
      } catch (error) {
        console.error("Error loading printer models:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (state.selectedManufacturers.length > 0) {
      fetchModels();
    }
  }, [state.selectedManufacturers]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredModels(availableModels);
      return;
    }

    const results = availableModels.filter((model) =>
      model.model.toLowerCase().includes(query.toLowerCase()),
    );
    setFilteredModels(results);
  };

  const handleModelSelect = (model) => {
    updateState({ selectedModel: model });
  };

  if (state.selectedManufacturers.length === 0) {
    return html`
      <div class="flex items-center justify-center p-4">
        <span class="text-gray-500"
          >${getTranslation("steps.step_2.no_models")}</span
        >
      </div>
    `;
  }

  const renderSpecifications = (specs, model) => {
    if (!specs) return null;

    const formatSpecValue = (specObj) => {
      if (!specObj) return "";
      if (specObj.hasRange && specObj.min && specObj.max) {
        return `${specObj.min}-${specObj.max}`;
      }
      if (specObj.max && !specObj.min) {
        return `≤ ${specObj.max}`;
      }
      if (specObj.min && !specObj.max) {
        return `≥ ${specObj.min}`;
      }
      return specObj.min || specObj.max || "";
    };

    const specs_array = [];

    if (specs.width) {
      specs_array.push(
        html`<span
          >${getTranslation("steps.step_2.specifications.width")}:
          ${formatSpecValue(specs.width)}mm</span
        >`,
      );
    }
    if (specs.diameter) {
      specs_array.push(
        html`<span
          >• ${getTranslation("steps.step_2.specifications.diameter")}:
          ${formatSpecValue(specs.diameter)}mm</span
        >`,
      );
    }
    if (specs.length) {
      specs_array.push(
        html`<span
          >• ${getTranslation("steps.step_2.specifications.length")}:
          ${formatSpecValue(specs.length)}m</span
        >`,
      );
    }

    if (specs_array.length === 0) return null;

    return html`
      <div
        class="${state.selectedModel?.handle === model.handle
          ? "text-white/80"
          : "text-gray-500"} flex flex-wrap gap-2 text-xs"
      >
        ${specs_array}
      </div>
    `;
  };

  return html`
    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <span class="text-sm md:text-base">
          ${getTranslation("steps.step_2.title", {
            manufacturers: state.selectedManufacturers
              .map((m) => m.name)
              .join(", "),
          })}
        </span>
      </div>

      <div class="relative">
        <input
          type="text"
          class="w-full rounded-md border border-gray-300 p-2 pr-8 text-sm transition-colors outline-none md:text-base"
          placeholder=${getTranslation("steps.step_2.search_placeholder")}
          value=${searchQuery}
          onInput=${(e) => handleSearch(e.target.value)}
        />
        ${searchQuery &&
        html`
          <button
            class="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer"
            onClick=${() => handleSearch("")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        `}
      </div>

      <!-- Models list -->
      <div class="max-h-[400px] overflow-y-auto">
        ${isLoading
          ? html`<${Spinner} size="lg" />`
          : html`
              <div class="grid grid-cols-1 gap-2">
                ${filteredModels.map(
                  (model) => html`
                    <button
                      key=${model.handle}
                      onClick=${() => handleModelSelect(model)}
                      class=${`w-full text-left px-3 md:px-4 py-2 rounded-md transition-colors duration-200 cursor-pointer border border-gray-200
                  ${
                    state.selectedModel?.handle === model.handle
                      ? "bg-[#6B8E23] text-white border-[#6B8E23]"
                      : "hover:bg-[#f0f7e6] bg-white"
                  }`}
                    >
                      <div class="flex items-center gap-2 md:gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke=${state.selectedModel?.handle === model.handle
                            ? "white"
                            : "currentColor"}
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="min-w-[20px] md:h-6 md:w-6 md:min-w-[24px]"
                        >
                          <polyline points="6 9 6 2 18 2 18 9"></polyline>
                          <path
                            d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"
                          ></path>
                          <rect x="6" y="14" width="12" height="8"></rect>
                        </svg>
                        <div class="flex flex-col gap-1">
                          <div class="flex items-center gap-1">
                            <span class="text-sm font-medium md:text-base"
                              >${model.model}</span
                            >
                            <span
                              class="${state.selectedModel?.handle ===
                              model.handle
                                ? "text-white/80"
                                : "text-subdued-text"} text-caption"
                            >
                              ${" - "} ${model.manufacturer}
                            </span>
                          </div>
                          ${renderSpecifications(model.specifications, model)}
                        </div>
                      </div>
                    </button>
                  `,
                )}
                ${filteredModels.length === 0 &&
                html`
                  <div
                    class="flex flex-col items-center gap-3 py-8 text-center text-gray-600"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="text-gray-400 md:h-10 md:w-10"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M8 15h8"></path>
                      <path d="M9 9h.01"></path>
                      <path d="M15 9h.01"></path>
                    </svg>
                    <span class="text-sm md:text-base">
                      ${getTranslation("steps.step_2.no_models")}
                    </span>
                  </div>
                `}
              </div>
            `}
      </div>
    </div>
  `;
}
